import { Component, EventEmitter, Input, Output, forwardRef } from '@angular/core';
import { NG_VALUE_ACCESSOR, ControlValueAccessor, AbstractControl, ValidationErrors, NG_VALIDATORS } from '@angular/forms';
import { MatIconModule } from "@angular/material/icon";
import { MatCardModule } from "@angular/material/card";
import { AppCheckbox } from "../app-checkbox/app-checkbox";
import { AppButtons } from "../app-buttons/app-buttons";
import { I18nTranslatePipe } from "../../core/pipes/i18n-translate.pipe";
import { AppDatepicker } from "../app-datepicker/app-datepicker";
import { AppRadioButton } from "../app-radio-button/app-radio-button";
import { AppInput } from "../app-input/app-input";
import { AppTooltipComponent } from "../app-tooltip/app-tooltip.component";

@Component({
  selector: 'app-file-upload',
  imports: [MatIconModule, MatCardModule, AppCheckbox, AppButtons, I18nTranslatePipe, AppDatepicker, AppRadioButton, AppInput, AppTooltipComponent],
  templateUrl: './file-upload.html',
  styleUrl: './file-upload.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FileUpload),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => FileUpload),
      multi: true
    }
  ]
})
export class FileUpload implements ControlValueAccessor {
  @Input() mode: 'modal' | 'inline' = 'modal';
  @Input() acceptedMimeTypes: string[] = ['image/jpeg', 'image/png', 'application/pdf'];
  @Input() acceptedExtensions = '.jpg,.jpeg,.png,.pdf';
  @Input() maxSizeMb = 2;
  @Input() dropZoneHint = 'JPG, PNG, PDF • Max 2MB';
  @Input() multiple = false;

  @Output() fileMeta = new EventEmitter<{ fileName: string; fileSize: string }>();
  @Output() fileChange = new EventEmitter<File | null>();
  @Output() filesChange = new EventEmitter<File[]>();
  @Output() extractRequested = new EventEmitter<void>();

  open = false;
  file?: File | null;
  files: File[] = [];
  progress = 0;
  isDragging = false;
  errorMessage = '';

  private onChange = (file: File | null) => { };
  private onTouched = () => { };

  writeValue(file: File | null): void {
    this.file = file ?? undefined;
    this.files = file ? [file] : [];
  }

  registerOnChange(fn: (file: File | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  onFileSelect(file: File): void {
    if (!this.isFormatAllowed(file)) {
      this.errorMessage = `Only ${this.getAllowedFormatsLabel()} files are allowed.`;
      return;
    }

    if (!this.isSizeAllowed(file)) {
      this.errorMessage = `File size must be ${this.maxSizeMb}MB or below.`;
      return;
    }

    this.errorMessage = '';

    if (this.multiple) {
      this.files = [...this.files, file];
      this.file = this.files[0] ?? null;
      this.onChange(this.file ?? null);
      this.fileChange.emit(this.file ?? null);
      this.filesChange.emit([...this.files]);
    } else {
      this.file = file;
      this.files = [file];
      this.onChange(file);
      this.fileChange.emit(file);
      this.filesChange.emit([file]);
    }

    this.onTouched();
    this.simulateUpload(file);
    this.fileMeta.emit({
      fileName: file.name,
      fileSize: `${(file.size / 1024).toFixed(1)} KB`
    });
  }

  simulateUpload(file: File): void {
    this.progress = 0;
    const interval = setInterval(() => {
      this.progress += 10;
      if (this.progress >= 100) {
        clearInterval(interval);
      }
    }, 200);
  }

  onBrowse(event: Event): void {
    const selectedFiles = (event.target as HTMLInputElement).files;
    if (!selectedFiles?.length) return;

    if (this.multiple) {
      Array.from(selectedFiles).forEach((file) => this.onFileSelect(file));
      return;
    }

    const file = selectedFiles[0];
    if (file) {
      this.onFileSelect(file);
    }
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.isDragging = false;

    const droppedFiles = event.dataTransfer?.files;
    if (!droppedFiles?.length) return;

    if (this.multiple) {
      Array.from(droppedFiles).forEach((file) => this.onFileSelect(file));
      return;
    }

    const file = droppedFiles[0];
    if (file) {
      this.onFileSelect(file);
    }
  }

  removeFile(index?: number): void {
    if (this.multiple && index !== undefined) {
      this.files = this.files.filter((_, i) => i !== index);
      this.file = this.files[0] ?? null;
      this.onChange(this.file ?? null);
      this.fileChange.emit(this.file ?? null);
      this.filesChange.emit([...this.files]);
      return;
    }

    this.file = null;
    this.files = [];
    this.progress = 0;
    this.errorMessage = '';
    this.onChange(null);
    this.fileChange.emit(null);
    this.filesChange.emit([]);
  }

  closeModal(): void {
    this.open = false;
    this.onTouched();
  }

  validate(control: AbstractControl): ValidationErrors | null {
    const file = control.value as File | null;

    if (!file) return null;

    if (!this.isFormatAllowed(file)) {
      return { invalidFormat: true };
    }

    const maxSize = this.maxSizeMb * 1024 * 1024;
    if (file.size > maxSize) {
      return { fileTooLarge: true };
    }

    return null;
  }

  onExtractClick(): void {
    this.onTouched();
    this.extractRequested.emit();
  }

  private isFormatAllowed(file: File): boolean {
    return this.acceptedMimeTypes.includes(file.type);
  }

  private isSizeAllowed(file: File): boolean {
    const maxSize = this.maxSizeMb * 1024 * 1024;
    return file.size <= maxSize;
  }

  private getAllowedFormatsLabel(): string {
    return this.acceptedExtensions
      .replaceAll('.', '')
      .split(',')
      .map((ext) => ext.trim().toUpperCase())
      .join(', ');
  }
}
